/**
 * Team Spain v0 — Servicio de PWA y Ciclo de Vida del Service Worker (M5)
 *
 * Gestiona el registro del Service Worker, la disponibilidad de instalación
 * y la detección controlada de actualizaciones sin recargas intempestivas en campo.
 */

type PwaUpdateListener = (hasUpdate: boolean) => void

class PwaService {
  private updateListeners: Set<PwaUpdateListener> = new Set()
  private deferredPrompt: any = null
  private registration: ServiceWorkerRegistration | null = null

  constructor() {
    this.init()
  }

  private init() {
    if (typeof window === 'undefined') return

    // Capturar evento de instalación PWA
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault()
      this.deferredPrompt = e
    })

    // Registrar Service Worker
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker
          .register('/sw.js')
          .then((reg) => {
            this.registration = reg

            // Comprobar si hay una versión esperando
            if (reg.waiting) {
              this.notifyUpdate(true)
            }

            // Escuchar nuevas versiones encontradas
            reg.addEventListener('updatefound', () => {
              const newWorker = reg.installing
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    this.notifyUpdate(true)
                  }
                })
              }
            })
          })
          .catch((err) => {
            console.warn('Error al registrar Service Worker:', err)
          })

        // Escuchar cuando el nuevo SW tome el control
        let refreshing = false
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (!refreshing) {
            refreshing = true
            window.location.reload()
          }
        })
      })
    }
  }

  /**
   * Suscribe a la notificación de nueva versión disponible
   */
  onUpdateAvailable(listener: PwaUpdateListener): () => void {
    this.updateListeners.add(listener)
    if (this.registration?.waiting) {
      listener(true)
    }
    return () => this.updateListeners.delete(listener)
  }

  private notifyUpdate(hasUpdate: boolean) {
    for (const listener of this.updateListeners) {
      try {
        listener(hasUpdate)
      } catch (err) {
        console.error(err)
      }
    }
  }

  /**
   * Aplica la actualización activando el nuevo SW de forma segura
   */
  applyUpdate(): void {
    if (this.registration?.waiting) {
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    } else {
      window.location.reload()
    }
  }

  /**
   * Permite solicitar la instalación al usuario si el navegador lo soporta
   */
  async promptInstall(): Promise<boolean> {
    if (!this.deferredPrompt) {
      return false
    }
    this.deferredPrompt.prompt()
    const { outcome } = await this.deferredPrompt.userChoice
    this.deferredPrompt = null
    return outcome === 'accepted'
  }

  isInstallable(): boolean {
    return Boolean(this.deferredPrompt)
  }
}

export const pwaService = new PwaService()
